import { fn } from '@common/utils';
import resolveToError = fn.resolveToError;
import reorderForColumns = fn.reorderForColumns;
import { constants as kconstants, NavKendoRegister } from '@common/navkendo';
import {
    NavPropertyItem, NavPropertyItems, NavPropertyValue, NavPropertyValues,
    INavEditContext, IPageMetaData, IViewProperty, IViewPropertyGroup
} from '@common/navdata';
import { IPersistentSetting } from '@common/services/settingsservice';
import { CreateEditControl } from './navpropertycontrolfactory';

export interface IGlobalPropertyGroupOptions {
    /** Ignore Missing Properties if not set: will be displayed with warning */
    IgnoreMissing?: boolean;
}

let _globalOptions: IGlobalPropertyGroupOptions = {};

/** Adjust Global Properties for property group generation */
export function AdjustGlobalProperties(props: Partial<IGlobalPropertyGroupOptions>) {
    fn.ExtendObject(_globalOptions, props);
}
/** Page Item Property Group Widget */
export class NavPageItemPropertyGroup extends kendo.ui.Widget implements INavEditContext {
    /** Add Dependent Dispose Action */
    AddDispose(cb: () => void) {
        this.bind(kconstants.NavDestroy, cb);
    }
    /** Add Settings by name; Dispatch to DockZones Settings */
    AddSetting<T>(name: string, s: IPersistentSetting<T>) {
        var dockParent = this.get_ParentEditContext();
        return (dockParent && dockParent.AddSetting(name, s)) || fn.nullOp;
    }
    GetAttachedParent(): JQuery {
        if (this.options.AttachedParent)
            return this.options.AttachedParent;
        var dockParent = this.get_ParentEditContext();
        return (dockParent && dockParent.GetAttachedParent()) || this.element;
    }
    private get_ParentEditContext() {
        return this.element.parent().closest(":data('INavEditContext')").data('INavEditContext') as INavEditContext;
    }
    /** Sorted control info  */
    private controlInfo: Array<{
        /** Visual Edit Control */
        Element: JQuery;
        /** Title of Element */
        Title: string;
        /** Index in Order of Properties */
        Idx: number;
        /* INderlying Display Value**/
        NavValue: NavPropertyValue;
    }>;
    /** Currently Visible Controls */
    private visibleControls: Array<{
        /** Visual Edit Control */
        Element: JQuery;
        /** Title of Element */
        Title: string;
        /** Index in Order of Properties */
        Idx: number;
        /* INderlying Display Value**/
        NavValue: NavPropertyValue;
    }>;
    private rawInfo: Array<{
        /** Visual Edit Control */
        Element: JQuery;
        /** Title of Element */
        Title: string;
        /** Index in Order of Properties */
        Idx: number;
        /** Current Index */
        CurrIdx: number;
        /** Current "Visual" Visible State*/
        Hidden: boolean;
        /* INderlying Display Value**/
        NavValue: NavPropertyValue;
    }>;
    private displayInfos: Array<{
        ValueCell: JQuery;
        LabelCell: JQuery;
        Visible: boolean;
        SetTitle(text: string): void;
    }>;

    /** Current Number of Invalid Inputs */
    public InvalidCount: number;

    public readonly options: INavPageItemPropertyGroupOptions;
    constructor(_element: any, options: INavPageItemPropertyGroupOptions & { $angular?: ng.IScope[] }) {
        super(_element, NormalizeOptions(options));
        options = this.options;
        options.numCols = options.numCols || 2;
        options.inColDirection = options.inColDirection == undefined ? true : !!options.inColDirection;
        var element = this.element;
        // Allow Hierarchical Access to EditContext from "inside"
        this.element.data("INavEditContext", this);

        let navPropertyValues: NavPropertyValues;
        const registerNavPropertyValues = (_values: NavPropertyValues, owned: boolean) => {
            if (navPropertyValues) {
                throw new Error("Already Created");
            }
            navPropertyValues = _values;
            var resetChange = fn.noop;
            const onChange = () => {
                resetChange();
                var currItem = _values.CurrentItem;
                resetChange = (currItem &&
                    currItem.Root.Anchor.InVisible.RegisterChanged(null, this.refreshVisibilityState.bind(this)))
                    || fn.noop;
            };
            _values.OnCurrentItemChanged(onChange);
            onChange();

            if (owned) {
                this.one(kconstants.NavDestroy, () => _values.destroy());
            }

            var createPropertyGrid = fn.proxy(this.options.NewLayout ? this.createPropertyGridMobile : this.createPropertyGrid, this);
            createPropertyGrid(navPropertyValues, (options.view && options.view.Properties) || ["Key"]);
        };
        const createNavPropertyValues = (MetaData: IPageMetaData) => {
            registerNavPropertyValues(new NavPropertyValues(MetaData), true);
        };
        const resolveFromValue = (source: TGroupOptionsSource) => {
            if (source instanceof NavPropertyValues) {
                registerNavPropertyValues(source, false);
            } else if (source instanceof NavPropertyValue) {
                let propValue = source;
                createNavPropertyValues(propValue.Property.ArrayProperties);
                // Refresh Items on Current Item Change
                this.one(kconstants.NavDestroy, source.OnCurrentItemChanged(() => {
                    var newLineData = propValue.CurrentItem && propValue.CurrentItem.Root.GetLineData(propValue.Property);
                    navPropertyValues.BindToContainerSelection(newLineData);
                }));
            } else if (source instanceof NavPropertyItems) {
                registerNavPropertyValues(new NavPropertyValues(source.MetaData), true);
                navPropertyValues.BindToContainerSelection(source);
            } else if (source instanceof NavPropertyItem) {
                registerNavPropertyValues(new NavPropertyValues(source.MetaData, source), true);
            } else {
                if (!(source && (source.PageName || source.PropertyName))) {
                    throw new Error("Missing Source for PropertyGroup");
                }
                registerNavPropertyValues(new NavPropertyValues(source), true);
            }
        };

        var source = options.item;
        if (typeof source == "string" || options.item === undefined) {
            var scope = (options.$angular && options.$angular[0]) || $(element).scope();
            if (scope) {
                const waitfunc = typeof source === "string" ? <any> source : (() => options.item);
                this.one(kconstants.NavDestroy,
                    scope.$watch<TGroupOptionsSource>(waitfunc, (newVal) => {
                        if (!newVal)
                            return;

                        if (!navPropertyValues) {
                            resolveFromValue(newVal);
                        }
                        var prop = newVal instanceof NavPropertyItem ? newVal : undefined;
                        if (prop) {
                            navPropertyValues.SetCurrentItem(prop);
                        }
                    }));
            } else {
                element.add($("<div></div>").text("Missing Scope[" + source + "]"));
            }
        } else {
            resolveFromValue(source);
        }
    }

    private createPropertyGrid(navPropertyValues: NavPropertyValues, props: Array<IViewProperty | string>) {
        let options = this.options,
            numCols = options.numCols || 2,
            element = this.element;
        try {
            if (!navPropertyValues)
                throw new Error("Initialisierung nur mit gültigem Context ('NavPageItem'/'IPageMetaData') - Element möglich");

            var navRawValues = props.map(p => {
                if (typeof p == "string")
                    return { Name: p };
                else
                    return p;
            }).map(pView => {
                return {
                    Property: navPropertyValues.GetPropertyValue(pView.Name),
                    Name: pView.Name,
                    Details: pView.Details,
                    ExplicitTitle: pView.Title
                };
            }).filter(item => !options.DesignMode && ! _globalOptions.IgnoreMissing || item.Property),
                currRow;

            var navOrderValues = this.rawInfo = navRawValues.map((item, idx) => {
                return {
                    Idx: idx,
                    CurrIdx: NaN,
                    Hidden: undefined,
                    NavValue: item.Property,
                    Name: item.Name,
                    Title: item.ExplicitTitle || (item.Property ? item.Property.Title : ""),
                    ValueCell: undefined,
                    LabelCell: undefined,
                    Element: item.Property ? CreateEditControl(item.Property, this, { CustomDetailCols: item.Details, Mobile : options.Mobile, NewLayout : options.NewLayout, DesignMode: options.DesignMode }) : $("<div>").text(item.Name)
                        .attr("title", "Eigenschaft '" + item.Name + "' nicht gefunden auf '" + navPropertyValues.MetaData.PageName + "'")
                        .css("background-color", "red"),
                };
            });
            if (options.inColDirection) {
                navOrderValues = reorderForColumns(navOrderValues, numCols);
            }

            var controlInfo = this.controlInfo = this.visibleControls = navOrderValues,
                singleInfo = controlInfo.length == 1 && controlInfo[0],
                singleGrid = singleInfo && singleInfo.NavValue && singleInfo.NavValue.Property && !!singleInfo.NavValue.Property.ArrayProperties,
                values = fn.distinct(this.controlInfo.map(item => item.NavValue)).filter(fn.isSet);

            // SetupInvalidCount Changed tracking and notification
            var invalidChanged = (() => {
                var invalidCount = values.filter(item => item.Invalid).length;
                if (invalidCount != this.InvalidCount) {
                    this.InvalidCount = invalidCount;
                    this.trigger("InvalidCountChanged", { InvalidCount: invalidCount });
                }
            }).bind(this);
            values.forEach(item => item.bind("Invalid", invalidChanged));
            this.bind(kconstants.NavDestroy, () => values.forEach(v => v.unbind("Invalid", invalidChanged)));
            invalidChanged();

            // Create Table From Controls
            if (controlInfo.length == 0) {
                // No Props?
            } else if (singleGrid) {
                // single Grid Prop: No Title
                $(element).append(controlInfo[0].Element);
            } else {
                // Add padding to table
                this.element.addClass("idms_Panel_Padding");

                var table = $("<table class='navTable'></table>");
                this.displayInfos = controlInfo.map((c, idx) => {
                    if (!(idx % numCols)) {
                        currRow = $('<tr>');
                        table.append(currRow);
                    } else {
                        // Add separator col
                        currRow.append($('<td class="navSepCell"></td>'));
                    }
                    c.CurrIdx = idx;
                    c.Hidden = false;
                    var result = {
                        ValueCell: $('<td class="navValCell">').append(c.Element),
                        LabelCell: $('<td class="navLblCell">').text(c.Title),
                        Visible: true,
                        SetTitle: (title) => result.LabelCell.text(title)
                    };
                    currRow.append(result.LabelCell, result.ValueCell);
                    return result;
                });

                $(element).append(table);
            }

            controlInfo.forEach(c => c.Element.on("keydown.nav", fn.proxy(this._keyDown, this)), this);

            // Set Up Visibility State Change Interaction
            this.refreshVisibilityState();
        } catch (err) {
            element.append($("<div>").text(resolveToError(err)));
        }
    }

    private createPropertyGridMobile(navPropertyValues: NavPropertyValues, props: Array<IViewProperty | string>) {
        let options = this.options,
            numCols = options.numCols || 2,
            element = this.element;
        try {
            if (!navPropertyValues)
                throw new Error("Initialisierung nur mit gültigem Context ('NavPageItem'/'IPageMetaData') - Element möglich");

            var navRawValues = props.map(p => {
                if (typeof p == "string")
                    return { Name: p };
                else
                    return p;
            }).map(pView => {
                return {
                    Property: navPropertyValues.GetPropertyValue(pView.Name),
                    Name: pView.Name,
                    Details: pView.Details,
                    ExplicitTitle: pView.Title
                };
            }).filter(item => !options.DesignMode && !_globalOptions.IgnoreMissing || item.Property);

            let currNavLabelRow: JQuery;
            let currNavCtrlRow: JQuery;

            var navOrderValues = this.rawInfo = navRawValues.map((item, idx) => {
                return {
                    Idx: idx,
                    CurrIdx: NaN,
                    Hidden: undefined,
                    NavValue: item.Property,
                    Name: item.Name,
                    Title: item.ExplicitTitle || (item.Property ? item.Property.Title : ""),
                    Element: item.Property ? CreateEditControl(item.Property, this, { CustomDetailCols: item.Details, Mobile : options.Mobile, NewLayout: options.NewLayout, DesignMode: options.DesignMode }) : $("<div>").text(item.Name)
                        .attr("title", "Eigenschaft '" + item.Name + "' nicht gefunden auf '" + navPropertyValues.MetaData.PageName + "'")
                        .css("background-color", "red"),
                };
            });
            if (options.inColDirection) {
                navOrderValues = reorderForColumns(navOrderValues, numCols);
            }

            let controlInfo = this.controlInfo = this.visibleControls = navOrderValues,
                rowInfos: { Items: JQuery[] }[] = [],
                currRowInfo: { Items: JQuery[] },
                singleInfo = controlInfo.length == 1 && controlInfo[0],
                singleGrid = singleInfo && singleInfo.NavValue && singleInfo.NavValue.Property && !!singleInfo.NavValue.Property.ArrayProperties,
                values = fn.distinct(this.controlInfo.map(item => item.NavValue)).filter(fn.isSet);

            // SetupInvalidCount Changed tracking and notification
            var invalidChanged = (() => {
                var invalidCount = values.filter(item => item.Invalid).length;
                if (invalidCount != this.InvalidCount) {
                    this.InvalidCount = invalidCount;
                    this.trigger("InvalidCountChanged", { InvalidCount: invalidCount });
                }
            }).bind(this);
            values.forEach(item => item.bind("Invalid", invalidChanged));
            this.bind(kconstants.NavDestroy, () => values.forEach(v => v.unbind("Invalid", invalidChanged)));
            invalidChanged();

            // Create Table From Controls
            if (controlInfo.length == 0) {
                // No Props?
            } else if (singleGrid) {
                // single Grid Prop: No Title
                $(element).append(controlInfo[0].Element);
            } else {

                if (!this.options.Mobile) {
                  // Add padding to table
                  this.element.addClass("idms_Panel_Padding");
                }

                var table = $("<table class='navMTable'></table>");
                this.displayInfos = controlInfo.map((c, idx) => {
                    if (!(idx % numCols)) {
                        currNavLabelRow = $(`<tr class="navMLabelRow">`);
                        currNavCtrlRow = $(`<tr class="navMCtrlRow">`);
                        let currCount = Math.min(numCols, controlInfo.length - idx);
                        let sepRow = $(`<tr class="navMSepRow"><td colspan="${currCount}"></td></tr>`);
                        currRowInfo = { Items : [sepRow, currNavCtrlRow, currNavLabelRow] };
                        rowInfos.push(currRowInfo);
                        table.append(currNavLabelRow, currNavCtrlRow, sepRow);
                    }
                    c.CurrIdx = idx;
                    c.Hidden = false;
                    let lbl: JQuery;
                    var result = {
                        ValueCell: $('<td class="">').append(c.Element),
                        LabelCell: $(`<td></td>`).append(lbl = $('<label></label>').text(c.Title)),
                        SetTitle: title => lbl.text(title),
                        Visible: true,
                    };
                    result.LabelCell.appendTo(currNavLabelRow);
                    result.ValueCell.appendTo(currNavCtrlRow);
                    c.Element.addClass("navMValue");
                    return result;
                });

                $(element).append(table);
            }

            controlInfo.forEach(c => c.Element.on("keydown.nav", fn.proxy(this._keyDown, this)), this);

            this.bind(CONTROL_VISIBILITY_CHANGED, () => {
                // Refresh visibility of "separator rows"
                var visSepRowCount = this.visibleControls.length / numCols;
                rowInfos.forEach((item, idx) => item.Items.forEach(jItem => jItem.toggle(idx < visSepRowCount)));
            });
            // Set Up Visibility State Change Interaction
            this.refreshVisibilityState();
        } catch (err) {
            element.append($("<div>").text(resolveToError(err)));
        }
    }
    /**  refresh Visibility state from underlying Property Values State */
    refreshVisibilityState() {
        var controlInfo = this.rawInfo,
            options = this.options;

        if (!this.displayInfos) {
            // not a grid Display (e.g. Single Item
            return;
        }
        // Check if Pending Changes
        var noChanges = controlInfo.every(item => item.NavValue && item.Hidden == item.NavValue.Hidden);
        if (noChanges)
            return;

        var visibleControls = controlInfo.filter(item => !item.NavValue || !item.NavValue.Hidden);
        var hiddenControls = controlInfo.filter(item => item.NavValue && item.NavValue.Hidden);

        if (options.inColDirection)
            visibleControls = reorderForColumns(visibleControls, options.numCols);

        for (var i = 0; i < hiddenControls.length; i++) {
            var cInfo = hiddenControls[i];
            var currIdx = i + visibleControls.length;
            var dInfo = this.displayInfos[currIdx];
            if (cInfo.Hidden !== true || cInfo.Idx != currIdx) {
                cInfo.Element.remove();
                cInfo.CurrIdx = currIdx;
                cInfo.Hidden = true;
            }

            if (dInfo.Visible !== false) {
                dInfo.LabelCell.hide();
                dInfo.ValueCell.hide();
                dInfo.Visible = false;
            }
        }

        for (var i = 0; i < visibleControls.length; i++) {
            var cInfo = visibleControls[i];
            var dInfo = this.displayInfos[i];
            if (cInfo.CurrIdx != i || cInfo.Hidden !== false) {
                dInfo.SetTitle(cInfo.Title);
                dInfo.ValueCell.append(cInfo.Element);

                cInfo.CurrIdx = i;
                cInfo.Hidden = false;
            }

            if (dInfo.Visible !== true) {
                dInfo.LabelCell.show();
                dInfo.ValueCell.show();
                dInfo.Visible = true;
            }
        }

        this.visibleControls = visibleControls;

        this.trigger(CONTROL_VISIBILITY_CHANGED);
    }

    destroy() {
        // First trigger explicit Dispose Actions
        this.trigger(kconstants.NavDestroy);
        var controlInfo = this.controlInfo;
        if (controlInfo) {
            controlInfo.forEach(c => { c.Element.off(".nav"); delete c.NavValue; });
            controlInfo.length = 0;
        }
        // we attached this in constructor!
        this.element.removeData("INavEditContext");

        super.destroy();
    }

    private _keyDown(e: JQueryEventObject) {
        if (e.which == kendo.keys.TAB) {
            var controlInfo = this.visibleControls;
            var back = e.shiftKey,
                currEl = fn.FindFirst(controlInfo, c => ($.contains(c.Element[0], e.target) || (c.Element[0] == e.target)));

            if (currEl) {
                if (back)
                    var m = controlInfo.filter(item => item.Idx < currEl.Idx && item.NavValue && !item.NavValue.ReadOnly).sort((a, b) => a.Idx - b.Idx).pop();
                else
                    var m = controlInfo.filter(item => item.Idx > currEl.Idx && item.NavValue && !item.NavValue.ReadOnly).sort((a, b) => a.Idx - b.Idx)[0];
                if (m) {
                    var el = $(".k-input", m.Element);
                    if (el.length == 0)
                        el = m.Element;

                    currEl.Element.blur();
                    var firstElem = el.first();
                    if (firstElem) {
                        firstElem.focus().select();
                    }
                    // WTF: DDList does not get handled properly in Chrome, IE otherwise ...
                    const ddList = m.Element.find(":data(kendoDropDownList)").data("kendoDropDownList");
                    if (ddList) {
                        ddList.focus();
                    }

                    e.preventDefault();
                } else {
                    // TODO : too much assumption
                    // (Problems on Navigation ... INF083082)
                    var nextDock = this.element.parents(':data(kendoNavDock)').next().data("kendoNavDock");
                    if (nextDock) {
                        var fm = nextDock.element.find(":focusable")[0];
                        if (fm) {
                            currEl.Element.blur();
                            $(fm).focus();
                            e.preventDefault();
                        }
                    }
                }
            }
        }
    }
}
NavKendoRegister(<any>NavPageItemPropertyGroup, "NavPageItemPropertyGroup");

const CONTROL_VISIBILITY_CHANGED = "CONTROL_VISIBILITY_CHANGED";

type TGroupOptionsSource = NavPropertyValues | NavPropertyItem | IPageMetaData | NavPropertyItems | NavPropertyValue;

function NormalizeOptions(options: INavPageItemPropertyGroupOptions) {
    let NewLayout: keyof INavPageItemPropertyGroupOptions = "NewLayout";
    let Mobile: keyof INavPageItemPropertyGroupOptions = "Mobile";
    if (!(Mobile in options)) {
        options.Mobile = false;
    } else {
        options.Mobile = !!options.Mobile;
    }
    if (!(NewLayout in options)) {
        options.NewLayout = options.Mobile;
    }

    // options.NewLayout = true;
    return options;
}

/** Options to Create a Page Item Property Group */
export interface INavPageItemPropertyGroupOptions {
    /** Source to display Properties from */
    item: TGroupOptionsSource | string;
    /** Description of the Items to Display */
    view: IViewPropertyGroup;
    /** Number Of Columns in Table TODO : this prop does not get consumed from Server config so far (as was in asp!) */
    numCols?: number;
    /** ReOrder Properties in Column Direction TODO : this prop does not get consumed from Server config so far (as was in asp!) */
    inColDirection?: boolean;
    /** we need 'future' Dom context to Create child Items (ng - Context for Lookup Controls at the moment) in Scenarios where a group is created "unattached" */
    AttachedParent?: JQuery;
    /** Is Mobile Version of our UI */
    Mobile?: boolean;
    /** Force new Layout */
    NewLayout?: boolean;

    DesignMode?: boolean;
}

declare global {
    interface JQuery {
        /** Display a NAV Property Group for a Page Item */
        kendoNavPageItemPropertyGroup(options: INavPageItemPropertyGroupOptions): JQuery;
        data(key: "kendoNavPageItemPropertyGroup"): NavPageItemPropertyGroup;
    }
}
